import { useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useUnit } from 'effector-react';
import { useTranslation } from 'react-i18next';
import { $cardLimitStore, $cardStore } from '@lib/store';
import { useCardFlow, useRouteHook } from '@shared/hooks';
import { cn, PaletteUtils, PermissionUtils, RoutePaths } from '@shared/utils';
import { AccountInfo, Users, CardSidebar, TransactionsTable, View, PageContainer } from '@widgets';

export const CardPage = () => {
  const params = useParams<{ id: string }>();
  const id = Number(params.id);
  const { navigate } = useRouteHook();

  const cardStore = useUnit($cardStore.store);

  const cardFlow = useCardFlow(id);

  const { t } = useTranslation('card');

  useEffect(() => {
    $cardStore.events.getCard({ id });
    return () => $cardStore.events.clear();
  }, [id]);

  useEffect(() => {
    if (cardStore.loading) return;
    if (cardStore.error !== 'Action is forbidden.') return;
    $cardStore.events.clear();
    navigate(RoutePaths.error.onNavigate(), { replace: true });
  }, [cardStore.loading, cardStore.error]);

  useEffect(() => {
    if (!cardStore.data) return;
    if (cardFlow?.viewCardLimit) $cardLimitStore.events.getLimit({ id: cardStore.data.cardId, cardKind: cardStore.data.cardKind });
    return () => $cardLimitStore.events.clear();
  }, [cardStore.data, cardFlow?.viewCardLimit]);

  return (
    <PageContainer title={t('title')} back backText={t('backText')} return>
      <View loading={cardStore.loading} noData={!cardStore.data} error={cardStore.error} onRefresh={() => $cardStore.events.getCard({ id })}>
        {cardStore.data && (
          <div className={cn('flex gap-6', 'max-xl:flex-col')}>
            <div
              className={cn(
                'flex min-w-0 flex-1 flex-col',
                PaletteUtils.isInsigniaPalette && 'gap-6',
                PaletteUtils.isVcfcPalette && 'rounded-lg border border-gray-black bg-white dark:bg-soft-black',
              )}
            >
              <AccountInfo card={cardStore.data} />
              {PermissionUtils.viewUsers() && <Users cardId={cardStore.data.cardId} />}
              {cardFlow?.viewTransactions && <TransactionsTable cardId={cardStore.data.cardId} />}
            </div>
            <CardSidebar card={cardStore.data} />
          </div>
        )}
      </View>
    </PageContainer>
  );
};
